import React from 'react';
import styled, { css } from 'styled-components';
import { isEmpty } from 'lodash';
import { ItemType, ItemTypePill, selectClasses } from 'venn-ui-kit';
import { type AnalysisSubject, Dates, isHistoricalPortfolio } from 'venn-utils';
import type { SearchMenuItem } from '../types';
import type { FrequencyEnum } from 'venn-api';
import { cn } from '@/lib/utils';

/** The padding between each cell in the menu table. */
export const CELL_SPACING = 10;

export const getItemTrackingId = (item: SearchMenuItem) =>
  (item.searchResult?.fundId ?? item.searchResult?.portfolioId ?? item.searchResult?.tagIds)?.toString();

export const getItemType = (item: SearchMenuItem) => {
  if (!isEmpty(item.searchResult?.tagIds)) {
    return ItemType.Tag;
  }
  if (item.value?.isCompositeBenchmark) {
    return ItemType.Benchmark;
  }
  if (item.value?.type === 'private-portfolio') {
    return ItemType.PrivatePortfolio;
  }
  if (item.value?.type === 'private-investment') {
    return ItemType.PrivateInvestment;
  }
  if (item.value?.type === 'portfolio') {
    if (item.value.isAggregatedPortfolio) {
      return ItemType.LiveAggregatedPortfolio;
    }
    if (item.value.portfolio && isHistoricalPortfolio(item.value.portfolio)) {
      return ItemType.HistoricalPortfolio;
    }
    return ItemType.Portfolio;
  }

  if (item.value?.isCategory) {
    return ItemType.Investment;
  }

  if (item.category === 'view') {
    return item.viewInfo?.analysisViewType as ItemType;
  }

  if (item.category === 'article') {
    return ItemType.Article;
  }

  return ItemType.Investment;
};

export const ItemLabel = ({ item, isDisabled }: { item: SearchMenuItem; isDisabled: boolean }) => {
  return <ItemTypePill isDisabled={isDisabled} item={getItemType(item)} />;
};

/** For some reason composite benchmarks are counted as user uploaded, so check for this */
export const isUploaded = (item: SearchMenuItem) => !!item.value?.userUploaded && !item.value?.isCompositeBenchmark;

export const getTicker = (item: AnalysisSubject) => {
  const fund = item.fund;
  return fund?.symbol && !item.isCategory ? fund.symbol : '—';
};

export const getSubjectRange = (subject: AnalysisSubject, frequency: FrequencyEnum = 'YEARLY') => {
  const isPortfolio = subject.type === 'portfolio';
  const start = isPortfolio ? subject.portfolio?.periodStart : subject.fund?.startRange;
  const end = isPortfolio ? subject.portfolio?.periodEnd : subject.fund?.endRange;
  return start && end ? `${Dates.toDDMMMYYYY(start, frequency)} - ${Dates.toDDMMMYYYY(end, frequency)}` : '—';
};

export const HeaderCell = ({ children, className, ...rest }: React.HTMLProps<HTMLTableCellElement>) => (
  <th className={cn('pb-[8px] pt-[4px] text-center leading-[1]', className)} {...rest}>
    {children}
  </th>
);

export const ExpandHeaderCell = ({ children, className, ...rest }: React.HTMLProps<HTMLTableCellElement>) => (
  <HeaderCell className={cn('w-full', className)} {...rest}>
    {children}
  </HeaderCell>
);

export const COLLAPSE_ANIMATION_SIZE = 60;

export const LeftCollapseWrapper = styled.div<{ collapsed: boolean }>`
  margin-right: ${COLLAPSE_ANIMATION_SIZE}px;
  margin-left: -${COLLAPSE_ANIMATION_SIZE}px;
  transition: margin-right 0.3s;

  ${(props) =>
    props.collapsed &&
    css`
      visibility: hidden;
      margin-right: 0;
      margin-left: 0;
      width: 0;
      padding: 0;
      border: none;
    `}
`;

export const placeholder = ({
  customPlaceholder,
  condition,
  portfoliosOnly,
  investmentsOnly,
  smallScreen = false,
}: {
  customPlaceholder?: React.ReactNode;
  condition: boolean;
  portfoliosOnly: boolean;
  investmentsOnly: boolean;
  smallScreen?: boolean;
}) =>
  customPlaceholder ?? (
    <Placeholder className={selectClasses.PlaceholderText}>
      {getPlaceholderText(condition, portfoliosOnly, investmentsOnly, smallScreen)}
    </Placeholder>
  );

export const getPlaceholderText = (
  condition: boolean,
  portfoliosOnly: boolean,
  investmentsOnly: boolean,
  smallScreen: boolean,
) => {
  if (condition) {
    return 'Search';
  }
  if (portfoliosOnly) {
    return 'Search for any portfolio';
  }
  if (investmentsOnly) {
    return 'Search for any investment';
  }
  if (smallScreen) {
    return 'Search for any investment, portfolio, or tags';
  }
  return 'Search for any investment, portfolio, benchmark or category';
};

const Placeholder = ({ children, className, ...rest }: React.HTMLProps<HTMLDivElement>) => (
  <div className={cn('flex items-center leading-normal', className)} {...rest}>
    {children}
  </div>
);
